import { useEffect } from "react";

type CmdKey = "c" | "v" | "x" | "Backspace" | "z" | "redo" | "search";

/**
 * useShortCutListener - Hook to listen to keyboard shortcut events
 * @param callback - Callback function to be called when shortcut event is triggered
 * @example useShortCutListener((key) => {
    switch (key) {
      case "c":
        Logger.log("Copy");
        break;
      case "x":
        Logger.log("Cut");
        break;
      case "v":
        Logger.log("Paste");
        break;
      case "z":
        Logger.log("Undo");
        break;
      case "redo":
        Logger.log("Redo");
        break;
      case "Backspace":
        Logger.log("Backspace");
        break;
      default:
        break;
    }
  });
 */
export const useShortCutListener = (callback: (key: CmdKey) => void) => {
	useEffect(() => {
		const keyListener = (event: KeyboardEvent) => {
			if (!(event.metaKey || event.ctrlKey)) {
				return;
			}

			const key = event.key.toLowerCase();

			// Handle redo: Cmd/Ctrl+Shift+Z (Mac/Windows) or Ctrl+Y (Windows)
			if (key === "z" && event.shiftKey) {
				event.preventDefault();
				callback("redo");
				return;
			}

			if (key === "y" && !event.shiftKey) {
				event.preventDefault();
				callback("redo");
				return;
			}

			// Cmd/Ctrl+Shift+K: focus a page's search field
			if (key === "k" && event.shiftKey) {
				event.preventDefault();
				callback("search");
				return;
			}

			// Handle other shortcuts
			switch (key) {
				case "c":
				case "v":
				case "x":
				case "backspace":
					callback(event.key as CmdKey);
					break;
				case "z":
					// Only handle undo if shift is NOT pressed (redo is handled above)
					if (!event.shiftKey) {
						event.preventDefault();
						callback(event.key as CmdKey);
					}
					break;
				default:
					break;
			}
		};

		document.addEventListener("keydown", keyListener);

		// Cleanup the event listener on unmount
		return () => {
			document.removeEventListener("keydown", keyListener);
		};
	}, [callback]);
};
